import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, RotateCcw, Phone, MessageCircle, Mail, Instagram, MapPin, Bell, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getSiteSettings, updateSiteSettings, resetSiteSettings } from "@/lib/adminApi";

const FIELDS = [
  { key: "brand_name", label: "Marka adı", icon: null, placeholder: "Ankara Düğün Fotoğrafı", hint: "Header, footer, e-posta ve SEO şemalarında kullanılır." },
  { key: "phone", label: "Telefon", icon: Phone, placeholder: "0532 111 22 33", hint: "'Ara' butonları bu numarayı çevirir. 0 ile başlayan yazım otomatik +90 formatına çevrilir." },
  { key: "whatsapp", label: "WhatsApp numarası", icon: MessageCircle, placeholder: "Boş bırakılırsa telefon kullanılır", hint: "Farklı bir WhatsApp hattınız varsa girin." },
  { key: "email", label: "E-posta", icon: Mail, placeholder: "info@ankaradugunfotografi.com", hint: "Footer ve iletişim sayfasında görünür." },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "@kullaniciadi veya tam bağlantı", hint: "Kullanıcı adı yazmanız yeterli; bağlantıya çevrilir." },
  { key: "address", label: "Adres / Bölge", icon: MapPin, placeholder: "Çankaya, Ankara", hint: "Footer ve yerel işletme şemasında kullanılır." },
  { key: "map_query", label: "Harita araması", icon: MapPin, placeholder: "Ankara Düğün Fotoğrafı, Çankaya", hint: "Google Haritalar'da aranacak ifade." },
  { key: "notify_email", label: "Bildirim e-postası", icon: Bell, placeholder: "Boş bırakılırsa e-posta alanı kullanılır", hint: "Yeni teklif talepleri bu adrese bildirilir." },
];

export default function SettingsPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["admin-site-settings"], queryFn: getSiteSettings });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && !form) setForm({ ...data.values });
  }, [data, form]);

  const dirty = data && form && FIELDS.concat([{ key: "whatsapp_default_message" }]).some((f) => (form[f.key] || "") !== (data.values[f.key] || ""));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await updateSiteSettings(form);
      toast.success("İletişim bilgileri kaydedildi — site anında güncellendi");
      qc.invalidateQueries({ queryKey: ["admin-site-settings"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["admin-notify"] });
      setForm(null);
      return res;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
    return null;
  };

  const reset = async () => {
    if (!window.confirm("Tüm iletişim bilgileri varsayılan (yer tutucu) değerlere döndürülsün mü?")) return;
    try {
      await resetSiteSettings();
      toast.success("Varsayılanlara döndürüldü");
      qc.invalidateQueries({ queryKey: ["admin-site-settings"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
      setForm(null);
    } catch {
      toast.error("Sıfırlanamadı");
    }
  };

  if (isLoading || !form) {
    return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8A24A]" /></div>;
  }
  if (isError) {
    return <div className="py-16 text-center text-[#9A9387]">Ayarlar yüklenemedi. <button onClick={() => refetch()} className="text-[#D7B86A] underline">Tekrar dene</button></div>;
  }

  const eff = data.effective || {};
  const placeholder = data.is_placeholder_contact;

  return (
    <div className="space-y-8" data-testid="settings-panel">
      <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${placeholder ? "border-amber-500/30 bg-amber-500/10 text-amber-100" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"}`} data-testid="settings-status-banner">
        {placeholder ? <AlertTriangle className="w-5 h-5 shrink-0" /> : <CheckCircle2 className="w-5 h-5 shrink-0" />}
        <div>
          {placeholder
            ? "Sitede henüz yer tutucu iletişim bilgileri (+90 555 000 00 00) görünüyor. Gerçek numaranızı ve e-postanızı girip kaydedin."
            : "Gerçek iletişim bilgileriniz yayında. Değişiklikler kaydedildiği anda tüm sayfalara yansır."}
          {data.updated_at && <div className="text-xs opacity-70 mt-1">Son güncelleme: {new Date(data.updated_at).toLocaleString("tr-TR")}</div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#141517] p-5 sm:p-6 space-y-5" data-testid="settings-form">
          <h2 className="font-display text-2xl text-[#F7F2E8]">İletişim Bilgileri</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.key} className={f.key === "brand_name" ? "sm:col-span-2" : ""}>
                  <Label htmlFor={`st-${f.key}`} className="text-[#B8B1A5] text-xs flex items-center gap-1.5">
                    {Icon && <Icon className="w-3.5 h-3.5 text-[#C8A24A]" />} {f.label}
                  </Label>
                  <Input id={`st-${f.key}`} value={form[f.key] || ""} placeholder={f.placeholder}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    className="mt-1 bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid={`settings-input-${f.key}`} />
                  <p className="text-[11px] text-[#7C766B] mt-1">{f.hint}</p>
                </div>
              );
            })}
            <div className="sm:col-span-2">
              <Label htmlFor="st-wa-msg" className="text-[#B8B1A5] text-xs">WhatsApp varsayılan mesajı</Label>
              <Textarea id="st-wa-msg" rows={2} value={form.whatsapp_default_message || ""} maxLength={300}
                placeholder={data.defaults.whatsapp_default_message}
                onChange={(e) => setForm((s) => ({ ...s, whatsapp_default_message: e.target.value }))}
                className="mt-1 bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid="settings-input-whatsapp_default_message" />
              <p className="text-[11px] text-[#7C766B] mt-1">WhatsApp butonuna basıldığında hazır gelen metin.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button type="submit" disabled={saving || !dirty} className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold" data-testid="settings-save-button">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
            </Button>
            <Button type="button" variant="outline" onClick={() => setForm({ ...data.values })} disabled={!dirty}
              className="border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5" data-testid="settings-discard-button">
              Vazgeç
            </Button>
            <Button type="button" variant="ghost" onClick={reset} disabled={!data.overridden_fields?.length}
              className="ml-auto text-[#9A9387] hover:text-red-300 hover:bg-red-500/10" data-testid="settings-reset-button">
              <RotateCcw className="w-4 h-4 mr-2" /> Varsayılanlara dön
            </Button>
          </div>
        </form>

        <aside className="rounded-2xl border border-white/10 bg-[#141517] p-5 space-y-4" data-testid="settings-preview">
          <h3 className="font-display text-xl text-[#F7F2E8]">Sitede görünen</h3>
          <p className="text-xs text-[#9A9387]">Kayıtlı değerlerin sitede kullanılan hali.</p>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-[#7C766B] text-xs">Marka</dt>
              <dd className="text-[#EDE7DA]" data-testid="preview-brand">{eff.brand_name}</dd>
            </div>
            <div>
              <dt className="text-[#7C766B] text-xs">Telefon (Ara)</dt>
              <dd><a href={`tel:${eff.phone_raw}`} className="text-[#D7B86A] hover:underline" data-testid="preview-phone">{eff.phone}</a></dd>
            </div>
            <div>
              <dt className="text-[#7C766B] text-xs">WhatsApp</dt>
              <dd><a href={`https://wa.me/${eff.whatsapp}`} target="_blank" rel="noreferrer" className="text-[#D7B86A] hover:underline inline-flex items-center gap-1" data-testid="preview-whatsapp">wa.me/{eff.whatsapp} <ExternalLink className="w-3 h-3" /></a></dd>
            </div>
            <div>
              <dt className="text-[#7C766B] text-xs">E-posta</dt>
              <dd className="text-[#EDE7DA] break-all" data-testid="preview-email">{eff.email}</dd>
            </div>
            <div>
              <dt className="text-[#7C766B] text-xs">Instagram</dt>
              <dd><a href={eff.instagram} target="_blank" rel="noreferrer" className="text-[#D7B86A] hover:underline break-all" data-testid="preview-instagram">{eff.instagram}</a></dd>
            </div>
            <div>
              <dt className="text-[#7C766B] text-xs">Adres</dt>
              <dd className="text-[#EDE7DA]" data-testid="preview-address">{eff.address}</dd>
            </div>
            <div>
              <dt className="text-[#7C766B] text-xs">Bildirim alıcısı</dt>
              <dd className="text-[#EDE7DA] break-all" data-testid="preview-notify">{eff.notify_email || eff.email}</dd>
            </div>
          </dl>
          <a href="/iletisim" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#9A9387] hover:text-white">
            İletişim sayfasını aç <ExternalLink className="w-3 h-3" />
          </a>
        </aside>
      </div>
    </div>
  );
}
